"use client";

import { useInitializeGoogleMap } from "@/hooks/useInitializeGoogleMap";
import { useMapMarkers } from "@/hooks/useMapMarkers";
import { usePreloadImages } from "@/hooks/usePreloadedImages";
import { useTranslations } from "next-intl";
import type { Coords, MapPlace } from "@/types/places";
import { coordsToLatLng, latLngToCoords, roundCoord } from "@/utils/location";
import { useEffect, useMemo, useRef } from "react";

interface GoogleMapProps {
  places: MapPlace[];
  currentLocation: Coords | null;
  googleMapsApiKey?: string;
  showCurrentLocation?: boolean;
  onPlaceSelected?: (location: MapPlace) => void;
  onBoundsChanged?: (center: Coords) => void;
  renderMarker?: (
    place: MapPlace,
    isHovered: boolean,
    isActive: boolean,
    onMouseEnter: () => void,
    onMouseLeave: () => void,
  ) => React.ReactElement;
}

export default function GoogleMapBase({
  places,
  currentLocation,
  googleMapsApiKey,
  showCurrentLocation = true,
  onBoundsChanged = () => {},
  onPlaceSelected = () => {},
  renderMarker,
}: GoogleMapProps) {
  const t = useTranslations();
  const lastPannedRef = useRef<Coords | null>(null);

  usePreloadImages(places.map((it) => it.image));

  const { map, mapRef, mapsInitialized, error, isLoading } =
    useInitializeGoogleMap({
      apiKey: googleMapsApiKey,
      mapOptions: {
        zoomControl: true,
        fullscreenControl: true,
      },
    });

  // Memoize callbacks to prevent unnecessary marker re-renders
  const markerCallbacks = useMemo(
    () => ({
      onPlaceSelected,
      onPlaceHover: () => {}, // No hover functionality in base map
    }),
    [onPlaceSelected],
  );

  // Use custom marker rendering if provided
  useMapMarkers({
    map,
    mapsInitialized,
    places: renderMarker ? places : [],
    selectedPlaceId: null,
    hoveredPlaceId: null,
    callbacks: markerCallbacks,
    renderMarker: renderMarker || (() => <div />),
  });

  useEffect(() => {
    if (!map || !currentLocation || !showCurrentLocation || renderMarker)
      return;

    const currentLocationMarker =
      new window.google.maps.marker.AdvancedMarkerElement({
        position: coordsToLatLng(currentLocation),
        map,
        title: t("googleMap.currentLocation"),
      });

    return () => {
      currentLocationMarker.map = null;
    };
  }, [map, currentLocation, showCurrentLocation, t, renderMarker]);

  // Pan to selected location
  useEffect(() => {
    if (!map || !currentLocation) return;
    const last = lastPannedRef.current;
    if (
      last?.lat === currentLocation.lat &&
      last?.lon === currentLocation.lon
    ) {
      return;
    }
    map.panTo(coordsToLatLng(currentLocation));
    map.setZoom(15);
    lastPannedRef.current = currentLocation;
  }, [map, currentLocation]);

  // Detect user panning in the map
  useEffect(() => {
    if (!map) return;

    const listener = map.addListener("idle", () => {
      const center = map.getCenter();
      if (!center) return;

      const newCoords = latLngToCoords({
        lat: center.lat(),
        lng: center.lng(),
      });

      const last = lastPannedRef.current;

      // Notify only if coords changed and not already the last known pan (rounded)
      if (
        !last ||
        roundCoord(newCoords.lat) !== roundCoord(last.lat) ||
        roundCoord(newCoords.lon) !== roundCoord(last.lon)
      ) {
        lastPannedRef.current = newCoords;
        onBoundsChanged(newCoords);
      }
    });

    return () => listener.remove();
  }, [map, onBoundsChanged]);

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
      <div ref={mapRef} className="w-full h-[400px] relative">
        {isLoading && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <p className="text-gray-500">{t("googleMap.loading")}</p>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 p-6">
            <div className="text-center max-w-md">
              <p className="text-sm text-gray-600">{error}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
